<template>
  <div :class="elem.classes" :style="elem.styles">
    <b-dropdown
      ref="dropdown"
      :variant="variant"
      :class="elemDropdown.classes"
      :toggle-class="elemToggle.classes"
      v-bind="elemDropdown.bindMore"
      :readonly="elemDropdown.readonly"
      :disabled="elemDropdown.disabled"
      :boundary="elemDropdown.boundary"
      @show="onDropdownShow"
    >
      <!-- Button -->
      <template #button-content>
        <span :class="elemSelected.classes" :title="elemSelected.title" v-html="elemSelected.content"></span>
      </template>

      <!-- Options -->
      <div class="dropdown-menu-inner">
        <perfect-scrollbar ref="scrollbar" :options="{ wheelPropagation:false}">
          <!-- Options Group -->
          <template v-if="optionGroup">
            <template v-for="(option, index) in options">
              <b-dropdown-group
                :key="`optiongroup_${index}`"
                class="mb-5"
                :header="renderOptionGroupContent(option[_optionValue])"
              >
                <template v-for="(option2, index2) in (option.children || option.child)">
                  <b-dropdown-item
                    :class="{'d-none': isHide(option2[_optionValue])}"
                    :key="`option_${index2}`"
                    :active="isActive(option2[_optionValue])"
                    :disabled="isDisabled(option2[_optionValue])"
                    @click="onChangeSelected(option2[_optionValue])"
                  >
                    <div class="dropdown-item-label" :style="option2[_optionStyle]" v-html="renderOptionContent(option2[_optionValue])" :title="option2[_optionLabel]" />
                  </b-dropdown-item>
                </template>
              </b-dropdown-group>
            </template>
          </template>

          <!-- Options Normal -->
          <template v-else>
            <template v-for="(option, index) in options">
              <b-dropdown-divider v-if="option[_optionValue] === 'custom'" class="dropdown-divider" :key="`option_divider_custom_${index}`"></b-dropdown-divider> 
              <b-dropdown-item
                :class="{'d-none': isHide(option[_optionValue])}"
                :key="`option_${index}`"
                :active="isActive(option[_optionValue])"
                :disabled="isDisabled(option[_optionValue])"
                @click="onChangeSelected(option[_optionValue])"
              >
                <div class="dropdown-item-label" :style="option[_optionStyle]" v-html="renderOptionContent(option[_optionValue])" :title="option[_optionLabel]" />
              </b-dropdown-item>
            </template>
          </template>

          <!-- Options Empty -->
          <b-dropdown-text 
            v-if="isEmpty"
            class="mwe-normalselect-option"
          >
            <div class="dropdown-item-label" :title="_emptyText">{{ _emptyText }}</div>
          </b-dropdown-text>

        </perfect-scrollbar>
      </div>
      
    </b-dropdown>
  </div>
</template>

<script>
import _ from 'lodash';
import { PerfectScrollbar } from "vue2-perfect-scrollbar";

export default {
  components: {
    PerfectScrollbar,
  },
  props: {
    /**
     * Value that selected
     */
    selected: {
      required: true,
    },
    
    /**
     * List of option 
     */
    options: {
      type: Array,
      required: true,
      default: () => [],
    },

    /**
     * List of option's value that are disabled
     */
    optionsDisabled: {
      type: Array,
      default: () => [],
    },

    /**
     * Do options have group?
     */
    optionGroup: {
      type: Boolean,
      default: false,
    },


    /**
     * Key of option's value
     * Ex. 'value', 'id'
     */
    optionValue: {
      type: String,
      default: undefined,
    },

    /**
     * Key of option's label
     * Ex. 'label', 'text'
     */
    optionLabel: {
      type: String,
      default: undefined,
    },

    /**
     * Key of option's style
     * Ex. 'label', 'text'
     */
    optionStyle: {
      type: [String, Object],
      default: undefined,
    },

    /**
     * Key of option's icon
     * Ex. 'icon'
     */
    optionIcon: {
      type: String,
      default: undefined,
    },

    /**
     * Key of option's disabled
     * Ex. 'disabled'
     */
    optionDisabled: {
      type: String,
      default: undefined,
    },

    /**
     * If selected doesn't appear in options, this value will be used for default and change selected
     */
    notMatchChange: {
      type: [Object, String],
      default: undefined,
    },

    /**
     * If selected doesn't appear in options, this value will be used for default but not change selected
     */
    notMatchDefault: {
      type: [Object, String, Number],
      default: undefined,
    },

    /**
     * If selected doesn't appear in options, this text will be shown on toggle
     */
    notMatchText: {
      type: String,
      default: undefined,
    },

    /**
     * Advance for append content of option's label
     */
    conditions: {
      type: Array,
      default: () => [],
    },

    /**
     * Tool is readonly
     */
    readonly: {
      type: Boolean,
      default: false,
    },

    /**
     * Tool is disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * Tool's width is full of parent
     */
    block: {
      type: Boolean,
      default: true,
    },

    /**
     * Tool's boundary
     * That is Boostrap Dropdown's boundary
     */
    boundary: {
      type: String,
      default: undefined,
    },

    /**
     * Toggle's alignment
     * Acceptable values:
     *   - 'left'
     *   - 'center'
     *   - 'right'
     */
    textAlign: {
      type: String,
      default: 'left',
    },

    /**
     * Toggle's padding left-right
     * Acceptable values:
     *   - 'default'
     *   - 'small'
     */
    paddingSize: {
      type: String,
      default: "default",
    },

    /**
     * Menu's drop
     * That is Boostrap Dropdown's drop
     */
    menuDrop: {
      type: String,
      default: undefined,
      validator(value) {
        return ['left', 'right', 'dropright', 'dropleft'].includes(value);
      },
    },

    /**
     * Menu's max width
     */
    menuMaxWidth: {
      type: String,
      default: undefined,
    },

    /**
     * Menu's max height
     */
    menuMaxHeight: {
      type: String,
      default: undefined,
    },

    /**
     * Positions to display option's icon
     * Acceptable values:
     *   - true - Positions are option and selected
     *   - 'option' - Position is option only
     *   - 'selected' - Position is selected only
     */
    iconShow: {
      type: [Boolean, String, Array],
      default: false,
    },

    /**
     * Unit's content
     * That will append of option's label only
     * Ex. 'px'
     */
    unit: {
      type: String,
      default: undefined,
    },

    /**
     * Positions to display unit for all option
     * Acceptable values:
     *   - true - Positions are option and selected
     *   - 'option' - Position is option only
     *   - 'selected' - Position is selected only
     */
    unitShow: {
      type: [Boolean, String, Array],
      default: true,
    },

    /**
     * Tool's boundary
     */
    variant: {
      type: String,
      default: 'select',
    },

    /**
     * Language that use for tool's label
     * That will be used with engine label
     * Acceptable values:
     *   - 'en'
     *   - 'id'
     *   - 'th'
     */
    language: {
      type: String,
      default: 'en',
    },

    /**
     * custom empty text
     */
    emptyText: {
      type: String,
      default: undefined,
    }
  },
  data() {
    return {
      rootOptions: {},
      default: {
        menuDrop: undefined,
        menuMaxWidth: undefined, // unset
        menuMaxHeight: undefined, // 70vh
      },
      isEmpty: false,
      isSidebarArea: undefined, // ex. if dropdown in sidebar will force dropdown-menu to right
    };
  },
  computed: {
    // Element
    elem() {
      return { 
        classes: {
          'mwe-normalselect': true,
          'mwe-normalselect-block': this.block,
          'position-relative': true,
        }, 
        styles: {
          '--menu-max-width': this.menuMaxWidth || this.default.menuMaxWidth,
          '--menu-max-height': this.menuMaxHeight || this.default.menuMaxHeight,
        }
      };
    },
    elemDropdown() {
      return { 
        classes: {
          'dropdown-select': true,
          'dropdown-select-px-sm': Boolean(this.paddingSize == 'small'),
          'dropdown-select-block': this.block,
        },
        bindMore: {
          [this._menuDrop]: Boolean(this._menuDrop),
        },
        readonly: this.readonly,
        disabled: this.disabled,
        boundary: this.boundary,
      };
    },
    elemToggle() {
      return { 
        classes: {
          [`text-${this.textAlign}`]: true,
        } 
      };
    },
    elemSelected() {
      return { 
        classes: {
          'selected': true,
        }, 
        content: this.renderSelectedContent(),
        title: this.renderSelectedTitle(),
      };
    },

    // Root (Overwrite props)
    _optionsDisabled() {
      return this.optionsDisabled;
    },
    _selected() {
      return this.selected;
    },
    _notMatchChange() {
      return this.notMatchChange;
    },
    _notMatchDefault() {
      return this.notMatchDefault;
    },
    _notMatchText() {
      if (this.notMatchText !== undefined && this.notMatchText !== null) return this.notMatchText;
      return this.$t("engine.common.normalselect.choose", this.language);
    },
    _optionValue() {
      return this.optionValue || 'value';
    },
    _optionLabel() {
      return this.optionLabel || 'label';
    },
    _optionStyle() {
      return this.optionStyle || 'style';
    },
    _optionIcon() {
      return this.optionIcon || 'icon';
    },
    _optionDisabled() {
      return this.optionDisabled || 'disabled';
    },
    _iconShow() {
      let iconShow = [];
      if (typeof this.iconShow === "boolean") iconShow = this.iconShow ? ['selected', 'option'] : [];
      if (typeof this.iconShow === "string") iconShow = this.iconShow.split(',');
      return iconShow.reduce((acc,curr) => (acc[curr.trim()]=true, acc), {});
    },
    _unitShow() {
      let unitShow = [];
      if (typeof this.unitShow === "boolean") unitShow = this.unitShow ? ['selected', 'option'] : [];
      if (typeof this.unitShow === "string") unitShow = this.unitShow.split(',');
      return unitShow.reduce((acc,curr) => (acc[curr.trim()]=true, acc), {});
    },
    _menuDrop() {
      return this.menuDrop || this.default.menuDrop;
    },
    _emptyText() {
      if(this.emptyText) return this.emptyText;
      return this.$t("engine.common.normalselect.empty", this.language)
    },

    optionSelected() {
      let option = this.getOption(this._selected);

      if (option !== undefined) return option;
      
      if (this._notMatchChange !== undefined) {
        option = this.getOption(this._notMatchChange);
        this.onChangeSelected(this.getValue(option));
        return option;
      }
      
      if (this._notMatchDefault !== undefined) {
        option = this.getOption(this._notMatchDefault);
        return option;
      }

      return option;
    }
  },
  methods: {
    normalizedOptions(options) {
      let _options = {};

      if (options) {
        if (this.optionGroup) {
          const optionsGroup = options;

          optionsGroup.forEach(optionGroup => {
            let _valueGroup = '_group_' + this.getValueNode(optionGroup);
            _options[_valueGroup] = {
              ...optionGroup,
              _value: _valueGroup,
            };

            const children = optionGroup.children || optionGroup.child || [];
            children.forEach(option => {
              let _value = this.getValueNode(option);
              _options[_value] = {
                ...option,
                _value: _value,
                _parentValue: _valueGroup,
                _disabled: Boolean(this._optionsDisabled.includes(_value)),
              };
            });
          });

        } else {
          options.forEach(option => {
            let _value = this.getValueNode(option);
            _options[_value] = {
              ...option,
              _value: _value,
              _disabled: Boolean(this._optionsDisabled.includes(_value)),
            };
          });
        }
      }

      this.isEmpty = Boolean( Object.keys(_options).filter((key) => { return Boolean(key !== undefined && key !== null && key !== 'null' && key !== 'blank') }).length == 0 );

      return _options;
    },
    initIsSidebarArea() {
      const elemDropdown = this.$refs.dropdown.$el;
      const elemSidebar = elemDropdown.closest('.b-sidebar');
      this.isSidebarArea = Boolean(elemSidebar);
    },
    initDefaultMenuDrop() {
      if (this.isSidebarArea) this.default.menuDrop = 'right';
    },
    initDefaultMenuMaxWidth() {
      const elemDropdown = this.$refs.dropdown.$el;
      const elemSidebarBody = elemDropdown.closest('.overlay-body');
      const elemTabpane = elemDropdown.closest('.tab-pane');

      if (this.isSidebarArea) {
        const menu_gap_bound = 24;

        if (elemTabpane) {
          const tabpane_w = elemTabpane.offsetWidth;
          let menu_wx = tabpane_w - menu_gap_bound - menu_gap_bound;
          this.default.menuMaxWidth = `${menu_wx}px`;
        } else {
          const sidebarBody_w = elemSidebarBody.offsetWidth;
          let menu_wx = sidebarBody_w - menu_gap_bound - menu_gap_bound;
          this.default.menuMaxWidth = `${menu_wx}px`;
        }
      }
    },
    initDefaultMenuMaxHeight() {
      const elemDropdown = this.$refs.dropdown.$el;
      const elemSidebarBody = elemDropdown.closest('.overlay-body');
      const elemMenu = elemDropdown.querySelector('.dropdown-menu');

      if (this.isSidebarArea) {
        const menu_mt = parseInt(window.getComputedStyle(elemMenu).getPropertyValue('margin-top')); // 8
        const menu_mb = parseInt(window.getComputedStyle(elemMenu).getPropertyValue('margin-bottom')); // 8
        const menu_gap_bound = 24;

        // Downdown
        const sidebarBody_b = elemSidebarBody.getBoundingClientRect().bottom;
        const dropdown_b = elemDropdown.getBoundingClientRect().bottom;
        const menu_hx_ver_down = Math.abs(sidebarBody_b - dropdown_b) - menu_mt - menu_mb - menu_gap_bound;

        // Dropup
        const sidebarBody_t = elemSidebarBody.getBoundingClientRect().top;
        const dropdown_t = elemDropdown.getBoundingClientRect().top;
        const menu_hx_ver_up = Math.abs(sidebarBody_t - dropdown_t) - menu_mt - menu_mb - menu_gap_bound;

        let menu_hx = (menu_hx_ver_down > 150) ? menu_hx_ver_down : Math.max(menu_hx_ver_down, menu_hx_ver_up);
        this.default.menuMaxHeight = `${menu_hx}px`;
      }
    },

    // Option
    getOption(value) {
      let _value = value;
      // if (_value && _value.split("-")[0] == '0') {
      //   _value = '0';
      // }
      return this.rootOptions[_value];
    },
    getValue(option) { // a, _group_g
      return option ? option._value : undefined;
    },
    getValueNode(option) { // a
      return option ? option[this._optionValue] : undefined;
    },
    getLabel(option) {
      return option ? option[this._optionLabel] : '';
    },
    getIcon(option) {
      return (option && this._optionIcon) ? option[this._optionIcon] : '';
    },
    getMoreContent(option) {
      let content = '';
      if (option === undefined) return content;
      if (this.conditions.length > 0) {
        this.conditions.forEach((condition) => {
          if (
            option[condition.key] == condition.matchValue || 
            (
              condition.key === this._optionDisabled && 
              condition.matchValue === true &&
              this._optionsDisabled.includes(this.getValue(option))
            )
          ) {
            content += condition.content;
          }
        });
      }
      return content;
    },
    isActive(value) {
      return Boolean(this.getValue(this.optionSelected) == value);
    },
    isDisabled(value) {
      const option = this.getOption(value);
      return Boolean(option && (
        (option[this._optionDisabled] !== undefined && option[this._optionDisabled]) || 
        option._disabled
      ));
    },
    isHide(value) {
      const option = this.getOption(value);
      return option.hide
    },
    // Render
    renderLabel(option) {
      return this.getLabel(option);
    },
    renderIcon(option) {
      return `<span class="d-inline-block mr-5" style="width: 16px;"><i class="${this.getIcon(option)}"></i></span>`;
    },
    renderUnit() {
      return `<span class="text-hint ml-3">${this.unit}</span>`;
    },
    renderSelectedTitle() {
      const option = this.optionSelected;
      if (option === undefined) return this._notMatchText;
      let label = "";
      label += this.getLabel(option); // label
      label += (this._unitShow.selected && this.unit) ? this.unit : ""; // unit
      return label;
    },
    renderSelectedContent() {
      const option = this.optionSelected;
      if (option === undefined) return this._notMatchText;
      let label = "";
      label += (this._iconShow.selected) ? this.renderIcon(option) : ""; // icon
      label += this.renderLabel(option); // label
      label += (this._unitShow.selected && this.unit) ? this.renderUnit() : ""; // unit
      return label;
    },
    renderOptionContent(value) {
      const option = this.getOption(value);
      let label = "";
      label += (this._iconShow.option) ? this.renderIcon(option) : ""; // icon
      label += this.renderLabel(option); // label
      label += (this._unitShow.option && this.unit) ? this.renderUnit() : ""; // unit
      label += (this.conditions.length > 0) ? this.getMoreContent(option) : ""; // more content
      return label;
    },
    renderOptionGroupContent(value) {
      const option = this.getOption('_group_' + value);
      let label = "";
      label += this.renderLabel(option);
      return label;
    },

    // Navigation
    navigate(text) {
      // reset hover
      const $elemHovered = this.$refs.dropdown.$el.querySelector('.dropdown-item.hover');
      if ($elemHovered) {
        $elemHovered.classList.remove('hover'); 
      }

      // find
      const $label = this.$refs.dropdown.$el.querySelector(`.dropdown-item .dropdown-item-label[title^="${text}"]`);
      if (!$label) return;

      const $elem = $label.closest('.dropdown-item');
      const $scrollbar = this.$refs.scrollbar.$el;

      $scrollbar.scrollTop = 0; // reset
      $scrollbar.scrollTop = $elem.getBoundingClientRect().top - $scrollbar.getBoundingClientRect().top; // goto
      $elem.classList.add('hover'); // hover
    },

    // Event
    // scrollEnd() {},
    // scrollStrat() {},
    // scrollUp() {},
    // scrollDown() {},
    onDropdownShow(bvEvent) {
      if (this.readonly === true || document.getElementById(bvEvent.componentId).hasAttribute('readonly')) { // For readonly + focus .dropdown-toggle when 
        bvEvent.preventDefault();
        document.getElementById(bvEvent.componentId).getElementsByClassName('dropdown-toggle')[0].focus();
      }

      if (this.isSidebarArea) {
        // Default MenuMaxWidth
        this.initDefaultMenuMaxWidth();

        // Default MenuMaxHeight
        this.initDefaultMenuMaxHeight();
      }
    },
    onDocKeyDown(e) {
      this.navigate(e.key.toUpperCase());
    },
    onChangeSelected(value) {
      this.$emit("change", value);
    },
  },
  mounted() {
    // Init IsSidebarArea
    this.initIsSidebarArea();

    // Default MenuDrop
    this.initDefaultMenuDrop();

    this.$refs.dropdown.$el.addEventListener('keydown', this.onDocKeyDown);
  },
  beforeDestroy() {
    this.$refs.dropdown.$el.removeEventListener('keydown', this.onDocKeyDown);
  },
  watch: {
    options: {
      handler() {
        this.rootOptions = this.normalizedOptions(this.options);
      },
      deep: true,
      immediate: true,
    },
    optionsDisabled(newValue, oldValue) {
      if (!_.isEqual(newValue, oldValue)) { // javascript don't know two arrays equal
        this.rootOptions = this.normalizedOptions(this.options); 
      }
    }
  }
};
</script>

<style scope>
/* MWE Tools - Normalselect 1.3 */
.mwe-normalselect {
  display: inline-block;
}
.mwe-normalselect.mwe-normalselect-block {
  width: 100%;
}
/* Menu */
.mwe-normalselect .dropdown-select.dropdown .dropdown-menu {
  max-width: var(--menu-max-width);
  overflow: hidden;
}
.mwe-normalselect .dropdown-menu-inner {
  overflow: auto;
  overscroll-behavior: none;
}
.mwe-normalselect .dropdown-menu .ps {
  height: auto;
  max-height: calc(var(--menu-max-height) - 8px - 8px);
}
.mwe-normalselect .dropdown.readonly .btn-select,
.mwe-normalselect .dropdown[readonly] .btn-select {
  cursor: default;
}
.mwe-normalselect .dropdown-menu-inner .dropdown-divider {
border-top: 1px solid #DEE3E6;
}
.dropdown.readonly .btn-select:focus, 
.dropdown.readonly .btn-select.active, 
.dropdown.readonly .btn-select:active,
.dropdown[readonly] .btn-select:focus, 
.dropdown[readonly] .btn-select.active, 
.dropdown[readonly] .btn-select:active {
  border-color: transparent !important;
  box-shadow: none !important;
}
</style>
